import React, { useState, useRef, useEffect } from "react";
import publicAxios from "../../utils/publicAxios";
import "../../assets/scss/login.scss";
import AuthFooter from "../../components/layout/footer/AuthFooter";
import AuthMechanism from "../../components/auth/AuthMechanism";
import { Link } from "react-router-dom";
import { FaArrowRight, FaExclamationCircle } from "react-icons/fa";
import { reset_password } from "../../locales/en";
import { EMAIL_REGEX } from "../../utils/constants/appConstants";
import AuthHeader from "../../components/auth/AuthHeader";
import { getForgetPasswordURL } from "../../utils/constants/apiEndPoints";
import { LOGIN } from "../../route/paths";

const Reset = () => {
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [resent, setResent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const initialErrors = {
    email: "",
    general: ""
  };
  const [errors, setErrors] = useState(initialErrors);

  // Submitting replaces the form with the confirmation, which unmounts the
  // button that had focus. Move focus to the confirmation heading so a screen
  // reader announces the outcome, and so the next Tab carries on from here
  // instead of restarting at the header.
  const sentHeadingRef = useRef(null);
  useEffect(() => {
    if (emailSent && sentHeadingRef.current) sentHeadingRef.current.focus();
  }, [emailSent]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setErrors({ email: "", general: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return; // no double-submit

    let isValid = true;

    // Reset errors before validation
    setErrors({ email: "", general: "" });

    // Basic email validation
    if (!email) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: reset_password?.emailError
      }));
      isValid = false;
    } else if (!EMAIL_REGEX.test(email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: reset_password?.validEmailError
      }));
      isValid = false;
      setErrorMessage("");
    }

    if (isValid) {
      const userData = { email };
      const isResend = emailSent;
      setSending(true);
      // Emptying the live region first means a second resend still reads as a
      // change, so it is announced again rather than staying silent.
      setResent(false);
      try {
        const response = await publicAxios.post(getForgetPasswordURL, userData);

        if (response.status === 200) {
          // Persistent confirmation — the email is kept on screen so the user
          // can see where the link went (and resend without retyping).
          setEmailSent(true);
          setResent(isResend);
          setErrors({ email: "", general: "" });
          setErrorMessage("");
        }
      } catch (error) {
        if (error.response?.status === 403) {
          setErrorMessage(reset_password?.accountError);
        } else {
          setErrors({
            email: "",
            general: reset_password?.sendFailedError
          });
          setErrorMessage("");
        }
      } finally {
        setSending(false);
      }
    }
  };

  return (
    <div className="auth-viewport">
      <AuthHeader />
      <main className="auth-page">
        <div className="auth-layout">
          <AuthMechanism who="mjwebs" />
          <div className="auth-right-panel">
            <div className="auth-form-wrap">
              {emailSent ? (
                /* Persistent confirmation — the end of this flow is the part
                   users remember; it must not flash and vanish. */
                <>
                  <h1
                    className="auth-heading"
                    ref={sentHeadingRef}
                    tabIndex={-1}
                  >
                    Check your email
                  </h1>
                  <p className="auth-subheading">
                    {`We've sent a reset link to `}
                    <strong>{email}</strong>
                    {`. It can take a minute to arrive.`}
                  </p>
                  {/* A resend leaves this view identical — same heading, same
                      address — so the outcome reaches the user only through
                      this live region. It is always mounted and starts empty,
                      because assistive tech announces the text arriving, not
                      the element. */}
                  <p className="auth-resend-status" role="status">
                    {resent ? reset_password?.linkResent : ""}
                  </p>
                  {errorMessage && (
                    <div className="auth-form-error">{errorMessage}</div>
                  )}
                  {errors.general && (
                    <div className="auth-form-error">{errors.general}</div>
                  )}
                  <p className="auth-toggle-text auth-toggle-text--left">
                    {`Didn't get it? `}
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={sending}
                    >
                      {sending ? "Sending…" : "Resend the link"}
                    </button>
                  </p>
                </>
              ) : (
                <>
                  <h1 className="auth-heading">Reset your password</h1>
                  <p className="auth-subheading">
                    {`Enter your email and we'll send you a reset link.`}
                  </p>

                  {errorMessage && (
                    <div className="auth-form-error">{errorMessage}</div>
                  )}

                  <form
                    id="reset_form"
                    className="auth-page-form"
                    onSubmit={handleSubmit}
                  >
                    <div className="input-row">
                      <div className="input-group">
                        <label htmlFor="email">Email address</label>
                        <input
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          name="email"
                          id="email"
                          autoComplete="email"
                          onChange={handleEmailChange}
                          aria-invalid={Boolean(errors.email || errors.general)}
                        />

                        {errors.email && (
                          <p className="error-message">
                            <FaExclamationCircle
                              className="error-icon"
                              aria-hidden
                            />
                            <small>{errors.email}</small>
                          </p>
                        )}
                        {errors.general && (
                          <p className="error-message">
                            <FaExclamationCircle
                              className="error-icon"
                              aria-hidden
                            />
                            <small>{errors.general}</small>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        <button
                          type="submit"
                          className="auth-btn-secondary"
                          disabled={sending}
                        >
                          {sending ? (
                            <>
                              <span
                                className="auth-spinner"
                                aria-hidden="true"
                              />
                              Sending…
                            </>
                          ) : (
                            <>
                              {reset_password?.submit_btn} <FaArrowRight />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>

                  <p className="auth-toggle-text">
                    Remembered it? <Link to={LOGIN}>Log in</Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <AuthFooter />
    </div>
  );
};

export default Reset;
